import {
  Card, Group, Text, Title, Badge, Stack, Center, Button, Textarea, TextInput, Avatar, Box,
  ActionIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowLeft, IconPencil, IconSend, IconShieldCheck, IconAddressBook,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api, { fileUrl } from '../services/api';
import { formatPhone } from '../lib/format';
import { StaffPhotoControl } from '../components/StaffPhotoControl';
import { StaffDeleteButton } from '../components/StaffDeleteButton';
import { Specialty } from '../components/Specialty';
import { BlockControl } from '../components/BlockControl';
import { SectionCard } from '../components/SectionCard';
import { BlockLoader } from '../components/BlockLoader';
import { ContactsManager } from '../components/ContactsManager';
import { StaffEditModal } from '../components/StaffEditModal';
import { useAuth } from '../contexts/AuthContext';
import { canAccess, canManageRole } from '../lib/permissions';
import { StaffPayrollSection, apiErr } from '../components/payroll/StaffPayrollTabs';

interface StaffInfo {
  id: string; fullName: string; login: string; role: string; phone?: string | null; isActive: boolean; blockReason?: string | null;
  gender?: string; birthDate?: string | null; canEnterTestScores?: boolean; specialty?: string | null;
  phones?: { id: string; label: string; number: string; isPrimary: boolean }[];
  addresses?: { id: string; label: string; address: string; isPrimary: boolean }[];
  /** S3 kaliti yoki `sticker:<...>` — GET /staff/:id hali qaytarmaydi, qiymat
      foto yuklangach POST javobidan cache'ga yoziladi (StaffPhotoControl izohi) */
  avatar?: string | null;
}

export default function StaffDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [editOpen, editH] = useDisclosure(false);

  const { data: staff, isLoading } = useQuery({
    queryKey: ['staff', id],
    queryFn: async () => (await api.get<StaffInfo>(`/staff/${id}`)).data,
    enabled: !!id,
  });

  /**
   * Profilni tahrirlash — o'qituvchi kartochkasi bilan YAGONA modal (`StaffEditModal`, T2).
   * Ilgari bu yerda faqat F.I.Sh / jins / rol / tug'ilgan sana bo'lgan qisqa forma turardi:
   * login va parolni qayta tayinlash faqat o'qituvchi kartochkasida bo'lib, xodim
   * kartochkasidan foydalanuvchini to'liq tahrirlab bo'lmasdi (kanal topshirig'i #55).
   */
  const invalidateStaff = () => {
    qc.invalidateQueries({ queryKey: ['staff', id] });
    // Xodim TEACHER bo'lsa Teachers ro'yxati (teachers-page) va dropdownlar (teachers) ham yangilansin.
    qc.invalidateQueries({ queryKey: ['staff-all'] });
    qc.invalidateQueries({ queryKey: ['teachers-page'] });
    qc.invalidateQueries({ queryKey: ['teachers'] });
  };

  // Xodimga shaxsiy bildirishnoma
  const notifyForm = useForm({ initialValues: { title: '', body: '' } });
  const notifyMut = useMutation({
    mutationFn: (v: typeof notifyForm.values) => api.post('/notifications/send', { userId: id, title: v.title, body: v.body }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('staffPage.notifySent') }); notifyForm.reset(); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  if (isLoading) return <BlockLoader variant="card" />;
  if (!staff) return <Center h={300}><Text c="dimmed">{t('staffDetail.notFound')}</Text></Center>;
  // O'zidan yuqori roldagi xodimni (buxgalter uchun — direktor/admin) faqat ko'radi
  const boshqara = canManageRole(user?.role, staff.role);

  return (
    <Stack>
      <Group>
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/staff')}><IconArrowLeft size={18} /></ActionIcon>
        <Card style={{ flex: 1 }} p="md">
          <Group justify="space-between">
            <Group>
              {/* fileUrl stiker (`sticker:<...>`) uchun undefined qaytaradi — bosh harf ko'rinadi */}
              <Avatar color="brand" radius="xl" size={52} src={fileUrl(staff.avatar)} alt={staff.fullName}>{staff.fullName[0]?.toUpperCase()}</Avatar>
              <Box>
                <Title order={4}>{staff.fullName}</Title>
                <Group gap="xs">
                  <Badge variant="light">{t(`roles.${staff.role}`)}</Badge>
                  <Specialty value={staff.specialty} badge />
                  {!staff.isActive && <Badge color="gray">{t('block.blocked')}</Badge>}
                  {staff.canEnterTestScores && <Badge color="grape" variant="light">{t('examsPage.testTeacher')}</Badge>}
                  <Text size="sm" c="dimmed" ff="monospace">{staff.login}</Text>
                  {staff.phone && <Text size="sm" c="dimmed">{formatPhone(staff.phone)}</Text>}
                </Group>
                {/* Yuz tanish terminali uchun foto. invalidate EMAS — GET javobida
                    `avatar` yo'q, refetch endigina yozilgan qiymatni o'chirib yuborardi. */}
                {boshqara && (
                  <StaffPhotoControl
                    userId={id}
                    avatar={staff.avatar}
                    onChanged={(a) => qc.setQueryData<StaffInfo>(['staff', id], (old) => (old ? { ...old, avatar: a } : old))}
                  />
                )}
              </Box>
            </Group>
            {boshqara && (
              <>
                <Button leftSection={<IconPencil size={16} />} onClick={editH.open}>{t('teacherDetail.edit')}</Button>
                <StaffDeleteButton id={id} fullName={staff.fullName} backTo="/staff" />
              </>
            )}
          </Group>
        </Card>
      </Group>

      {/* Bloklash — o'zini emas (backend ham taqiqlaydi) */}
      {user?.id !== staff.id && boshqara && (
        <SectionCard title={t('teacherDetail.status')} icon={IconShieldCheck}>
          <BlockControl
            patchPath={`/staff/${id}`}
            isActive={staff.isActive}
            blockReason={staff.blockReason}
            onDone={() => qc.invalidateQueries({ queryKey: ['staff', id] })}
          />
        </SectionCard>
      )}

      {/*
        Kontaktlar — FAQAT KO'RISH. `StaffEditModal` telefon/manzilni "o'rniga qo'yish" (replace)
        usulida yuboradi; bittalab tahrirlaydigan `ContactsManager` bilan aralashsa qatorlar
        id lari kutilmaganda almashadi (o'qituvchi kartochkasidagi bilan bir xil qoida).
      */}
      <SectionCard
        title={t('teacherDetail.contacts')}
        icon={IconAddressBook}
        right={boshqara ? (
          <Button size="compact-sm" variant="subtle" leftSection={<IconPencil size={14} />} onClick={editH.open}>
            {t('teacherDetail.edit')}
          </Button>
        ) : undefined}
      >
        <ContactsManager owner={{ userId: id }} phones={staff.phones ?? []} addresses={staff.addresses ?? []} queryKey={['staff', id]} readOnly />
      </SectionCard>

      {/* Shaxsiy bildirishnoma */}
      <SectionCard title={t('staffPage.notifyTitle')} icon={IconSend}>
        <form onSubmit={notifyForm.onSubmit((v) => notifyMut.mutate(v))}>
          <Stack gap="xs">
            <Text size="xs" c="dimmed">{t('staffPage.notifyHint')}</Text>
            <TextInput label={t('staffPage.notifySubject')} required maxLength={120} {...notifyForm.getInputProps('title')} />
            <Textarea label={t('staffPage.notifyBody')} required autosize minRows={2} maxLength={500} {...notifyForm.getInputProps('body')} />
            <Group justify="flex-end">
              <Button type="submit" variant="light" leftSection={<IconSend size={16} />} loading={notifyMut.isPending}>{t('staffPage.send')}</Button>
            </Group>
          </Stack>
        </form>
      </SectionCard>

      {/* Maosh / smena / davomat / maosh tarixi — o'qituvchi sahifasi bilan umumiy */}
      <StaffPayrollSection userId={id} money={canAccess(user?.role, 'payments')} />
      {/* To'liq tahrirlash — o'qituvchi kartochkasi bilan YAGONA komponent (T2) */}
      <StaffEditModal
        opened={editOpen}
        onClose={editH.close}
        staff={staff}
        onSaved={invalidateStaff}
        title={t('teacherDetail.edit')}
      />
    </Stack>
  );
}
